import { useEffect } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, DateNavigator, getTodaysDate, useFormField } from '@frontend/design-system';
import { useSchedulePulseInsuranceTabContext } from '../../context';
import { FiltersContainer } from './FiltersContainer';
import { ScheduleInsuranceTabTrackingIds } from './trackingIds';

type InsuranceTabDateHeaderV3Props = {
  shouldRenderFilters?: boolean;
  renderAdditionalActionButton?: React.ReactNode;
};

export const InsuranceTabDateHeaderV3 = ({
  shouldRenderFilters,
  renderAdditionalActionButton,
}: InsuranceTabDateHeaderV3Props) => {
  const { t } = useTranslation('schedule-pulse');

  const todayDate = getTodaysDate('MM/DD/YYYY');

  const { selectedDate, setSelectedDate } = useSchedulePulseInsuranceTabContext();

  const dateField = useFormField(
    {
      type: 'datePicker',
      value: selectedDate || getTodaysDate('MM/DD/YYYY'),
    },
    [selectedDate]
  );

  const isTodayButtonDisabled = dayjs(dateField.value).isSame(dayjs(), 'day');

  useEffect(() => {
    if (dateField.value) {
      setSelectedDate(dateField.value);
    }
  }, [dateField.value, setSelectedDate]);

  return (
    <div css={insuranceTabDateHeaderV3Styles}>
      <Button
        variant='secondary'
        size='small'
        disabled={isTodayButtonDisabled}
        trackingId={ScheduleInsuranceTabTrackingIds?.todayButton}
        onClick={() => {
          setSelectedDate(todayDate);
          dateField.onChange({ target: { value: todayDate } } as React.ChangeEvent<HTMLInputElement>);
        }}
      >
        {t('Today')}
      </Button>
      <DateNavigator
        {...dateField}
        name='calendar-date'
        label=''
        trackingIds={{
          prevButton: ScheduleInsuranceTabTrackingIds?.datePickerPreviousButton,
          nextButton: ScheduleInsuranceTabTrackingIds?.datePickerNextButton,
          dateInput: ScheduleInsuranceTabTrackingIds?.datePickerInputField,
          datePicker: ScheduleInsuranceTabTrackingIds?.datePickerComponent,
        }}
      />

      <div css={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        {shouldRenderFilters && <FiltersContainer />}
        {renderAdditionalActionButton}
      </div>
    </div>
  );
};

const insuranceTabDateHeaderV3Styles = css({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.colors.white,
  padding: theme.spacing(1),
  boxShadow: theme.shadows.light,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  gap: theme.spacing(1),
});
